import '@ag-grid-enterprise/all-modules/dist/styles/ag-grid.css';
import '@ag-grid-enterprise/all-modules/dist/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';

import { ColumnApi } from '@ag-grid-community/core/dist/cjs/columnController/columnApi';
import { ColumnResizedEvent, ColumnVisibleEvent } from '@ag-grid-community/core/dist/cjs/events';
import { AgGridReact, AgGridReactProps } from '@ag-grid-community/react';
import {
  AllModules,
  ColDef,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
} from '@ag-grid-enterprise/all-modules';
import { TFunction } from 'i18next';
import React, { ReactNode, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';

import { AgGridSortModel } from '../../pages/FinancialData/shared/FinancialDataContext/FinancialDataContext.types';
import NoData from '../NoData';
import { useColumnsStorage } from './hooks/useColumnsStorage';
import { ColumnPreset } from './Presets/ColumnPresets';
import TableGridColumnPresets from './Presets/TableGridColumnPresets';
import BenchmarkCompanyLinkCellRenderer from './Renderers/BenchmarkCompanyLinkCellRenderer';
import BenchmarkInterestRateCellRenderer from './Renderers/BenchmarkInterestRateCellRenderer';
import BranchAddressCellRenderer from './Renderers/BranchAddressCellRenderer';
import ClickableCellRenderer from './Renderers/ClickableCellRenderer';
import CompanyAddressCellRenderer from './Renderers/CompanyAddressCellRenderer';
import DateCellRenderer from './Renderers/DateCellRenderer';
import ExternalLinkCellRenderer from './Renderers/ExternalLinkCellRenderer';
import FileDownloadCellRenderer from './Renderers/FileDownloadCellRenderer/FileDownloadCellRenderer';
import LinkCellRenderer from './Renderers/LinkCellRenderer';
import BenchmarkLinkCellRenderer from './Renderers/LinkCellRenderer/BenchmarkLinkCellRenderer';
import LinkOrNumberOrTextCellRenderer from './Renderers/LinkOrNumberOrTextCellRenderer';
import LinkOrNumberOrTextOrBooleanCellRenderer from './Renderers/LinkOrNumberOrTextOrBooleanCellRenderer';
import NumberCellRenderer from './Renderers/NumberCellRenderer';
import NumberOrTextCellRenderer from './Renderers/NumberOrTextCellRenderer';
import PercentageCellRenderer from './Renderers/PercentageCellRenderer';
import ReportDownloadCellRenderer from './Renderers/ReportDownloadCellRenderer';
import RowIndexCellRenderer from './Renderers/RowIndexCellRenderer';
import { getSelectAllColumn } from './Renderers/RowSelection/HeaderSelectAllRenderer/selectAllRenderer.utils';
import SelectRowRenderer from './Renderers/RowSelection/SelectRowRenderer';
import TopShareHoldersCellRenderer from './Renderers/TopShareHoldersCellRenderer';
import ValueOrUnknownCellRenderer from './Renderers/ValueOrUnknownCellRenderer';
import YearWithValueCellRenderer from './Renderers/YearWithValueCellRenderer';
import TableGridApiContextConstructor from './TableGridApiContext/TableGridApiContexConstructor';
import { TableGridApiContextProvider } from './TableGridApiContext/TableGridApiContext';
import TableGridContainer, { TableGridContainerProps } from './TableGridContainer';

export interface TableGridProps extends TableGridContainerProps {
  translationKey?: string;
  tableToolbar?: any;
  columns: MainColumns[];
  originalProps?: AgGridReactProps;
  getDatasource?: IServerSideDatasource;
  onGridReady?: (params: any) => void;
  refreshTable?: () => void;
  children?: ReactNode;
  isShowSideBar?: boolean;
  isSideBarCollapsedByDefault?: boolean;
  rowSelection?: boolean;
  suppressTableContextProvider?: boolean;
  columnsStorageId?: string;
  initialSelectedRows?: string[];
  onSelectionChanged?: (selectedRows: string[]) => void;
  rowSelectionIdGetter?: (data: any) => string;
  initialSortModel?: AgGridSortModel;
  systemDefaultFields?: string[];
  systemDefaultPresets?: ColumnPreset[];
}

export interface MainColumns extends ColDef {}

export enum CellRenderer {
  Link = 'Link',
  BenchmarkLinkCellRenderer = 'BenchmarkLinkCellRenderer',
  BenchmarkInterestRateCell = 'BenchmarkInterestRateCell',
  BenchmarkCompanyLink = 'BenchmarkCompanyLink',
  RowIndex = 'RowIndex',
  Number = 'Number',
  BranchAddress = 'BranchAddress',
  CompanyAddress = 'CompanyAddress',
  TopShareHolders = 'TopShareHolders',
  Percentage = 'Percentage',
  SelectRow = 'SelectRow',
  Date = 'Date',
  ReportDownload = 'ReportDownload',
  Clickable = 'Clickable',
  YearWithValue = 'YearWithValue',
  ExternalLink = 'ExternalLink',
  ValueOrUnknown = 'ValueOrUnknown',
  FileDownload = 'FileDownload',
  NumberOrText = 'NumberOrText',
  LinkOrNumberOrText = 'LinkOrNumberOrText',
  LinkOrNumberOrTextOrBoolean = 'LinkOrNumberOrTextOrBoolean',
}

export const frameworkComponents: Record<CellRenderer | string, React.FC<any>> = {
  [CellRenderer.Link]: LinkCellRenderer,
  [CellRenderer.BenchmarkLinkCellRenderer]: BenchmarkLinkCellRenderer,
  [CellRenderer.BenchmarkInterestRateCell]: BenchmarkInterestRateCellRenderer,
  [CellRenderer.BenchmarkCompanyLink]: BenchmarkCompanyLinkCellRenderer,
  [CellRenderer.ExternalLink]: ExternalLinkCellRenderer,
  [CellRenderer.RowIndex]: RowIndexCellRenderer,
  [CellRenderer.Number]: NumberCellRenderer,
  [CellRenderer.BranchAddress]: BranchAddressCellRenderer,
  [CellRenderer.CompanyAddress]: CompanyAddressCellRenderer,
  [CellRenderer.TopShareHolders]: TopShareHoldersCellRenderer,
  [CellRenderer.Percentage]: PercentageCellRenderer,
  [CellRenderer.SelectRow]: SelectRowRenderer,
  [CellRenderer.Date]: DateCellRenderer,
  [CellRenderer.ReportDownload]: ReportDownloadCellRenderer,
  [CellRenderer.Clickable]: ClickableCellRenderer,
  [CellRenderer.YearWithValue]: YearWithValueCellRenderer,
  [CellRenderer.ValueOrUnknown]: ValueOrUnknownCellRenderer,
  [CellRenderer.FileDownload]: FileDownloadCellRenderer,
  [CellRenderer.NumberOrText]: NumberOrTextCellRenderer,
  [CellRenderer.LinkOrNumberOrText]: LinkOrNumberOrTextCellRenderer,
  [CellRenderer.LinkOrNumberOrTextOrBoolean]: LinkOrNumberOrTextOrBooleanCellRenderer,
  TableGridColumnPresets: TableGridColumnPresets,
};

const TableGrid: React.FC<TableGridProps> = ({
  columns,
  translationKey,
  tableToolbar,
  originalProps,
  getDatasource,
  onGridReady,
  refreshTable,
  children,
  isShowSideBar = true,
  isSideBarCollapsedByDefault = true,
  rowSelection,
  initialSelectedRows,
  onSelectionChanged,
  rowSelectionIdGetter,
  suppressTableContextProvider = false,
  columnsStorageId,
  initialSortModel,
  systemDefaultFields,
  systemDefaultPresets,
}) => {
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [columnsApi, setColumnsApi] = useState<ColumnApi | null>(null);
  const defaultColumns = useMemo(
    () => getInitialColumns(columns, t, rowSelection, translationKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  );
  const { columnDefs, onColumnChanged } = useColumnsStorage({ defaultColumns, columnsStorageId });

  const onGridReadyFn = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setColumnsApi(params.columnApi);

    // DISABLE_SEARCH_RESULTS_SORTING should prevent session stored sort model from being applied
    // in case admin decides to turn off sorting feature and restart the app
    if (initialSortModel && !(window as any)?.env?.DISABLE_SEARCH_RESULTS_SORTING) {
      params.api.setSortModel(initialSortModel);
    }

    if (getDatasource) {
      params.api.setServerSideDatasource(getDatasource);
    }

    if (typeof onGridReady === 'function') {
      onGridReady(params);
    }
  };

  const onColumnResizedFn = (params: ColumnResizedEvent) => {
    params.api.resetRowHeights();
  };

  const onColumnVisibleFn = (params: ColumnVisibleEvent) => {
    onColumnChanged(params);
    params.api.resetRowHeights();
  };

  const columnsPanel = {
    id: 'columns',
    labelDefault: 'Columns',
    labelKey: 'columns',
    iconKey: 'columns',
    toolPanel: 'agColumnsToolPanel',
    toolPanelParams: {
      suppressRowGroups: true,
      suppressValues: true,
      suppressPivotMode: true,
    },
  };

  const presetsPanel = {
    id: 'tableGridColumnPresets',
    labelDefault: 'Presets',
    labelKey: 'Presets',
    iconKey: 'columns',
    toolPanel: 'TableGridColumnPresets',
    toolPanelParams: {
      columnsApi,
      gridApi,
      defaultColumns,
      columnsStorageId,
      systemDefaultFields,
      systemDefaultPresets,
    },
  };

  const render = () => {
    return (
      <TableGridApiContextConstructor
        gridApi={gridApi}
        rowSelection={rowSelection}
        initialSelectedRows={initialSelectedRows}
        onSelectionChanged={onSelectionChanged}
        rowSelectionIdGetter={rowSelectionIdGetter}
        refreshTable={refreshTable}
      >
        <TableGridContainer>
          {tableToolbar}
          <AgGridReact
            columnDefs={columnDefs}
            modules={AllModules}
            frameworkComponents={frameworkComponents}
            onGridReady={onGridReadyFn}
            onColumnVisible={onColumnVisibleFn}
            onColumnResized={onColumnResizedFn}
            suppressContextMenu={true}
            suppressCellSelection={true}
            overlayNoRowsTemplate={renderToStaticMarkup(<NoData />)}
            sideBar={
              isShowSideBar
                ? {
                    toolPanels: systemDefaultPresets
                      ? [columnsPanel, presetsPanel]
                      : [columnsPanel],
                    position: 'right',
                    defaultToolPanel: isSideBarCollapsedByDefault ? 'none' : 'columns',
                  }
                : false
            }
            {...originalProps}
          />
          {children}
        </TableGridContainer>
      </TableGridApiContextConstructor>
    );
  };

  return !suppressTableContextProvider ? (
    <TableGridApiContextProvider>{render()}</TableGridApiContextProvider>
  ) : (
    render()
  );
};

export default TableGrid;

function getInitialColumns(
  columns: MainColumns[],
  t: TFunction,
  rowSelection?: boolean,
  translationKey?: string,
): MainColumns[] {
  const cols = columns.map((col) => {
    col.headerName = col.headerName ?? t(`${translationKey}:${col.field}`);
    return col;
  });

  if (rowSelection) {
    cols.unshift(getSelectAllColumn(t) as any);
  }

  return cols;
}
